/* ==========================================================================
   NEFER Studio — the Collections transition

   A cream wash with the glyph drawing itself, on the way into the Collections page
   and on the way back out. The Collections page is the one page that is drawings
   rather than photographs, so arriving should feel like turning a page rather
   than loading a URL.

   FADE IN, FADE OUT. The handoff called for a veil that was opaque on its
   first frame with no fade-in at all. On a real page load that produced a
   visible jolt on the way out — cream appears instantly, then the document
   swaps under it — so this now fades. The whole thing reads as one continuous
   cross-fade across the two documents:

       leaving        fade UP to cream · glyph draws · hold · navigate
       arriving       already at cream (opaque on frame one, no flash) · fade DOWN

   The arrival veil is the only one that starts opaque, and it is server-
   rendered with `data-in` already on it. That is deliberate: a JS fade-in on
   arrival needs a timer to flip opacity, and a throttled timer can batch that
   flip with the first paint, showing the destination at partial opacity for a
   frame. Departing veils can fade because nothing is behind them yet.

   Two other traps, both still live:

   1. The opacity and the glyph's stroke-dashoffset are CSS TRANSITIONS, never
      @keyframes. A keyframe restarts from frame zero on every re-render, and
      the veil re-renders constantly during a navigation, so it would stick on
      frame one forever. All this file does is toggle data- attributes.

   2. requestAnimationFrame does not run in a background tab. Every step that
      must complete is raced by a setTimeout, so a tab switch mid-transition
      can never strand someone on a cream screen with the navigation unfired.

   Leaving the Collections page for a page that has no veil of its own would end on a
   hard cut from cream to the destination, so the departing side leaves a flag
   in sessionStorage and the destination fades a plain cream veil out on load.
   That is what closes the loop and makes "fade in, fade out" true in every
   direction.
   ========================================================================== */

(function () {
  'use strict';

  var ROOMS = '/pages/collections';
  var FLAG = 'nfVeilArrive';

  /* Base per-stroke offsets, same five paths and same draw order as the glyph
     everywhere else: stem → oval → crossbar → inner arc → base arch. Each is
     multiplied by drawScale. */
  var STROKES = [0, 180, 380, 620, 720];
  var LAST_STROKE = 720;

  var reduced = false;
  try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  function narrow() {
    try { return window.matchMedia('(max-width: 900px)').matches; } catch (e) { return false; }
  }

  /* `fade` is derived from when the glyph actually FINISHES drawing, so the
     veil can never begin lifting mid-stroke on any device or in either
     direction. Exits are shorter than entrances by design — arriving is an
     event, leaving shouldn't feel like shopping is gated. Mobile is shorter
     again: a three-second interstitial on a phone reads as a slow site. */
  function timings(direction) {
    if (reduced) return { fadeIn: 220, drawMs: 0, drawScale: 0, hold: 0, fade: 400 };
    if (direction === 'exit') {
      return narrow()
        ? { fadeIn: 340, drawMs: 380, drawScale: 0.42, hold: 180, fade: 520 }
        : { fadeIn: 360, drawMs: 460, drawScale: 0.50, hold: 200, fade: 560 };
    }
    return narrow()
      ? { fadeIn: 360, drawMs: 480, drawScale: 0.55, hold: 300, fade: 620 }
      : { fadeIn: 380, drawMs: 760, drawScale: 1.00, hold: 380, fade: 700 };
  }

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* Run fn on the second animation frame, raced by a timer so a background
     tab (where rAF is suspended) still gets there. */
  function soon(fn, ms) {
    var done = false;
    var go = function () { if (done) return; done = true; fn(); };
    requestAnimationFrame(function () { requestAnimationFrame(go); });
    setTimeout(go, ms || 90);
  }

  function prepare(veil, t, fadeMs) {
    veil.style.setProperty('--nf-veil-fade', fadeMs + 'ms');
    veil.style.setProperty('--nf-veil-draw', t.drawMs + 'ms');
    each(veil.querySelectorAll('.nf-glyph path'), function (p, i) {
      var base = STROKES[i] === undefined ? 0 : STROKES[i];
      p.style.transitionDelay = Math.round(base * t.drawScale) + 'ms';
    });
  }

  /* Under reduced motion this is a plain cream cross-fade with NO glyph at
     all — not a faster draw, no draw. The CSS hides it; skip the flip so
     nothing is scheduled against it either. */
  function draw(veil) { if (!reduced) veil.setAttribute('data-drawing', ''); }

  function drop(veil, fadeMs) {
    veil.removeAttribute('data-in');
    setTimeout(function () {
      if (veil.parentNode) veil.parentNode.removeChild(veil);
    }, fadeMs + 60);
  }

  function build(label, withGlyph) {
    var veil = document.createElement('div');
    veil.className = 'nf-veil';
    veil.setAttribute('data-nf-veil', '');
    veil.setAttribute('aria-hidden', 'true');

    if (withGlyph) {
      var source = document.querySelector('[data-nf-veil-template] .nf-glyph');
      if (source) veil.appendChild(source.cloneNode(true));

      var span = document.createElement('span');
      span.className = 'nf-veil__label';
      span.textContent = label || '';
      veil.appendChild(span);
    }

    document.body.appendChild(veil);
    return veil;
  }

  /* ------------------------------------------------------------------ *
   * 1. Arrival
   * ------------------------------------------------------------------ */

  function initArrival() {
    var veil = document.querySelector('[data-nf-veil]');

    if (veil) {
      /* Collections's own veil: server-rendered, already opaque. Draw the
         glyph, hold, then fade it out. */
      var t = timings('enter');
      prepare(veil, t, t.fade);
      soon(function () { draw(veil); });
      setTimeout(function () {
        drop(veil, t.fade);
      }, t.fadeIn + (LAST_STROKE * t.drawScale) + t.drawMs + t.hold);
      return;
    }

    /* Any other page reached FROM the Collections page: no glyph, just close the
       cross-fade so the arrival is not a hard cut from cream. */
    var flag = null;
    try { flag = sessionStorage.getItem(FLAG); } catch (e) {}
    if (!flag) return;
    try { sessionStorage.removeItem(FLAG); } catch (e) {}

    var fade = parseInt(flag, 10) || 560;
    var plain = build('', false);
    plain.setAttribute('data-in', '');
    plain.style.setProperty('--nf-veil-fade', fade + 'ms');
    soon(function () { drop(plain, fade); });
  }

  /* ------------------------------------------------------------------ *
   * 2. Departure
   * ------------------------------------------------------------------ */

  function onRooms() {
    return window.location.pathname.indexOf(ROOMS) !== -1;
  }

  function plainClick(e) {
    return !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0;
  }

  function internal(a) {
    if (!a || !a.getAttribute) return false;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    /* Anything that opens a drawer or an overlay is not a navigation. */
    if (a.hasAttribute('data-nf-open')) return false;
    return a.host === window.location.host;
  }

  function veilThenGo(label, direction, href) {
    var t = timings(direction);
    var veil = build(label, direction === 'exit');
    prepare(veil, t, t.fadeIn);

    var gone = false;
    var go = function () {
      if (gone) return;
      gone = true;
      if (direction === 'exit') {
        /* Hand the fade duration to the destination so it can finish the
           cross-fade instead of cutting straight to the page. */
        try { sessionStorage.setItem(FLAG, String(t.fade)); } catch (e) {}
      }
      window.location = href;
    };

    /* Fade up first — nothing may navigate until the cream is fully opaque,
       or the swap shows through at partial opacity. */
    soon(function () { veil.setAttribute('data-in', ''); });

    if (direction === 'exit') {
      soon(function () { draw(veil); }, 120);
      setTimeout(go, t.fadeIn + (LAST_STROKE * t.drawScale) + t.drawMs + t.hold);
    } else {
      /* Going TO the Collections page: that page renders and draws its own veil, so
         this one only has to reach cream before the document changes. */
      setTimeout(go, t.fadeIn + 40);
    }
  }

  function initDeparture() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || !plainClick(e) || !internal(a)) return;

      var here = onRooms();
      var goingToRooms = (a.pathname || '').indexOf(ROOMS) !== -1;

      if (goingToRooms && !here) {
        e.preventDefault();
        veilThenGo('Collections', 'enter', a.href);
        return;
      }

      if (here && !goingToRooms) {
        e.preventDefault();
        veilThenGo((a.textContent || '').trim(), 'exit', a.href);
      }
    });
  }

  function boot() {
    initArrival();
    initDeparture();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
